import { ErrorCode, ExtractComponentProp } from '@approvalmax/types';
import { AddIcon } from '@approvalmax/ui';
import {
    Box,
    Button,
    Divider,
    Flex,
    Form,
    Grid,
    Popup,
    Radio,
    Text,
    toast,
    useConfirmation,
} from '@approvalmax/ui/src/components';
import { routerHelpers } from '@approvalmax/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import random from 'lodash/random';
import range from 'lodash/range';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useController } from 'react-hook-form';
import { useMatchAmaxPayOpenBankingXeroBankAccount } from 'shared/data/webApp/v1';
import { GetXeroBankAccounts, integrationApiPaths, useCreateXeroBankAccounts } from 'shared/data/webApp/v1';

import { messages } from '../../MatchBankAccountWithXeroPopup.messages';
import { NewBankAccountCard } from '../NewBankAccountCard/NewBankAccountCard';
import { NewXeroBankAccountForm } from '../NewXeroBankAccountForm/NewXeroBankAccountForm';
import { NewXeroBankAccountFormWrapper } from '../NewXeroBankAccountFormWrapper/NewXeroBankAccountFormWrapper';
import { reconnectOrgPopupOpenState } from '../ReconnectOrgPopup/ReconnectOrgPopup.states';
import { XeroBankAccountCard } from '../XeroBankAccountCard/XeroBankAccountCard';
import { xeroReconnectWarningVisibleState } from '../XeroReconnectWarning/XeroReconnectWarning.states';
import { useSortedXeroBankAccounts } from './Content.hooks';
import type { ContentProps } from './Content.types';

export const Content: FC<ContentProps> = (props) => {
    const {
        companyId,
        openForm,
        setOpenForm,
        form,
        refetchBankAccounts,
        bankAccounts,
        isLoading,
        xeroBankAccounts,
        isLoadingXeroBankAccounts,
        onMatch,
    } = props;

    const queryClient = useQueryClient();
    const [newXeroAccountId, setNewXeroAccountId] = useState<string>();
    const setXeroReconnectWarningVisible = useSetAtom(xeroReconnectWarningVisibleState);
    const setReconnectOrgPopupOpen = useSetAtom(reconnectOrgPopupOpenState);
    const xeroCreateBankAccountFromPlacerRef = useRef<HTMLDivElement>(null);

    // controller used here because we need to make `bankAccountId` required
    const { field: bankAccountIdField } = useController({
        name: 'bankAccountId',
        rules: { required: true },
        control: form.control,
    });

    const { confirmation } = useConfirmation({
        confirmMessage: messages.areYouSureAboutCreatingXeroBA,
        confirmButtonMessage: messages.confirmCreatingXeroBA,
    });

    const selectedBankAccountId = form.watch('bankAccountId');
    const selectedXeroBankAccountId = form.watch('xeroBankAccountId');

    const {
        mutate: matchAmaxPayOpenBankingXeroBankAccount,
        isLoading: isLoadingMatchAmaxPayOpenBankingXeroBankAccount,
    } = useMatchAmaxPayOpenBankingXeroBankAccount({
        onSuccess: () => {
            toast.success(messages.matchedSuccessfully);
            void refetchBankAccounts();
            onMatch?.();
        },
    });

    const { mutate: createXeroBankAccounts, isLoading: isLoadingCreateXeroBankAccounts } = useCreateXeroBankAccounts(
        {
            onSuccess: (data, variables) => {
                setOpenForm(false);
                form.setValue('xeroBankAccountId', data.account.id);
                setNewXeroAccountId(data.account.id);

                const url = routerHelpers.pathToUrl(integrationApiPaths.xeroBankAccounts, variables.params?.path);

                // we have an issue from the BE-side w/ xero cache microservice, thus we can't
                // rely on immediate response w/ updated data, that's why we prefer to update local
                // cache manually and turn off fetching fresh xero bank accounts
                queryClient.setQueryData<GetXeroBankAccounts['response']>([url], (prevData) => ({
                    ...prevData,
                    items: [...(prevData?.items || []), data.account],
                }));
            },
            onError: (error) => {
                if (error.code === ErrorCode.E4500_XERO_PERMISSIONS_NOT_SUFFICIENT) {
                    setXeroReconnectWarningVisible(true);
                    setReconnectOrgPopupOpen(true);
                }
            },
        },
        { skipInvalidateQueries: true }
    );

    const selectedBankAccount = useMemo(
        () => bankAccounts.find((bankAccount) => bankAccount.id === selectedBankAccountId),
        [bankAccounts, selectedBankAccountId]
    );

    const selectedBankAccountCurrencyCode = selectedBankAccount?.currencyCode;
    const selectedBankAccountMatchedXeroBankAccountId = selectedBankAccount?.xeroBankAccount?.id;

    const sortedXeroBankAccounts = useSortedXeroBankAccounts({
        selectedBankAccount,
        xeroBankAccounts,
        newXeroAccountId,
        selectedBankAccountCurrencyCode,
        selectedBankAccountMatchedXeroBankAccountId,
    });

    useEffect(() => {
        form.setValue('xeroBankAccountId', selectedBankAccountMatchedXeroBankAccountId);
    }, [form, selectedBankAccountMatchedXeroBankAccountId]);

    const onSubmit = form.handleSubmit(({ bankAccountId, xeroBankAccountId }) => {
        if (xeroBankAccountId) {
            void matchAmaxPayOpenBankingXeroBankAccount({
                body: { bankAccountId: xeroBankAccountId },
                params: { path: { companyId, bankAccountId } },
            });
        }
    });

    const handleNewBankClick = useCallback<ExtractComponentProp<typeof NewBankAccountCard, 'onClick'>>(
        (id) => {
            bankAccountIdField.onChange(id);
        },
        [bankAccountIdField]
    );

    const handleOpenForm = useCallback(() => {
        setOpenForm(true);
        xeroCreateBankAccountFromPlacerRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, [setOpenForm]);

    const handleCloseForm = useCallback(() => {
        setOpenForm(false);
    }, [setOpenForm]);

    const handleCreateXeroBankAccount = useCallback<ExtractComponentProp<typeof NewXeroBankAccountForm, 'onSubmit'>>(
        ({ createNewXeroBankAccount, ...body }) => {
            void confirmation()
                .then(() => createXeroBankAccounts({ body, params: { path: { companyId } } }))
                .catch(() => {});
        },
        [companyId, confirmation, createXeroBankAccounts]
    );

    return (
        <>
            <Form form={form} onSubmit={onSubmit}>
                <Popup.Header
                    title={messages.matchBankAccountWithXero}
                    subTitle={
                        <Box spacing='16 0 0 0'>
                            <Grid gridTemplateColumns='280px auto'>
                                <Text font='label' fontSize='large' fontWeight='medium' color='midnight80'>
                                    {messages.matchEachBankAccount}
                                </Text>

                                <Flex justifyContent='space-between' alignItems='center'>
                                    <Text font='label' fontSize='large' fontWeight='medium' color='midnight80'>
                                        {messages.withXeroAccount}
                                    </Text>

                                    <Button
                                        noPadding
                                        startIcon={<AddIcon />}
                                        onClick={handleOpenForm}
                                        variant='text'
                                        color='blue100'
                                    >
                                        {messages.createNewXeroBankAccount}
                                    </Button>
                                </Flex>
                            </Grid>
                        </Box>
                    }
                    actions={
                        <Button
                            size='medium'
                            color='blue80'
                            type='submit'
                            disabled={
                                isLoadingMatchAmaxPayOpenBankingXeroBankAccount ||
                                !form.formState.isValid ||
                                // https://approvalmax.atlassian.net/browse/AM-17676 (case 2)
                                selectedBankAccountMatchedXeroBankAccountId === selectedXeroBankAccountId
                            }
                        >
                            {messages.confirm}
                        </Button>
                    }
                />

                <Popup.Body spacing='0 0 32 0'>
                    <Grid gridTemplateColumns='328px max-content auto'>
                        <Grid
                            gridAutoRows='max-content'
                            rowGap={16}
                            role='list'
                            maxHeight={550}
                            padding='0 16 0 32'
                            overflowY='auto'
                        >
                            {isLoading
                                ? range(0, random(1, 5)).map((index) => <NewBankAccountCard.Loading key={index} />)
                                : bankAccounts.map((bankAccount, index) => (
                                      <NewBankAccountCard
                                          key={index}
                                          {...bankAccount}
                                          selected={bankAccount.id === selectedBankAccountId}
                                          onClick={handleNewBankClick}
                                      />
                                  ))}
                        </Grid>

                        <Divider orientation='vertical' />

                        <Box overflowY='auto' maxHeight={550} spacing='0 32 0 16'>
                            <div ref={xeroCreateBankAccountFromPlacerRef} />

                            <Radio.Group required name='xeroBankAccountId' disabled={isLoading}>
                                <Grid gridAutoRows='max-content' rowGap={16} width='100%' role='list'>
                                    {/* INFO: we check both loading states because of edge case when we need to show a form
                                     when no xero accounts are available for current bank accounts set. In that case
                                      popup is blinking */}
                                    {isLoading || isLoadingXeroBankAccounts
                                        ? range(0, random(1, 5)).map((index) => (
                                              <XeroBankAccountCard.Loading key={index} />
                                          ))
                                        : sortedXeroBankAccounts.map((xeroBankAccount) => (
                                              <XeroBankAccountCard
                                                  key={xeroBankAccount.id}
                                                  {...xeroBankAccount}
                                                  disabled={
                                                      (xeroBankAccount.matched &&
                                                          xeroBankAccount.id !==
                                                              selectedBankAccountMatchedXeroBankAccountId) ||
                                                      selectedBankAccountCurrencyCode !== xeroBankAccount.currencyCode
                                                  }
                                                  matchedWithSelectedBankAccount={
                                                      xeroBankAccount.id === selectedBankAccountMatchedXeroBankAccountId
                                                  }
                                              />
                                          ))}
                                </Grid>
                            </Radio.Group>
                        </Box>
                    </Grid>
                </Popup.Body>
            </Form>

            <NewXeroBankAccountFormWrapper open={openForm} placerRef={xeroCreateBankAccountFromPlacerRef}>
                <NewXeroBankAccountForm
                    isLoading={isLoadingCreateXeroBankAccounts}
                    selectedBankAccount={selectedBankAccount}
                    onSubmit={handleCreateXeroBankAccount}
                    onClose={handleCloseForm}
                />
            </NewXeroBankAccountFormWrapper>
        </>
    );
};

Content.displayName = 'Content';
